import { useTranslation } from '@/i18n/hooks';
import { useAnimatedCounter } from '@/utils';

export interface MetricCardProps {
  translationKey:
    | 'metrics.hectares'
    | 'metrics.clients'
    | 'metrics.experience'
    | 'metrics.drones'
    | 'metrics.economy'
    | 'metrics.precision';
  icon: React.ComponentType<{ className?: string }>;
  value: number;
  suffix?: string;
  prefix?: string;
  decimals?: number;
  isInView: boolean;
  delay: number;
}

export function MetricCard({
  translationKey,
  icon: IconComponent,
  value,
  suffix,
  prefix,
  decimals,
  isInView,
  delay,
}: MetricCardProps) {
  const { t } = useTranslation('home');
  const animatedValue = useAnimatedCounter(value, isInView, 2000, decimals);

  return (
    <div
      className={`group relative rounded-2xl border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:border-primary/50 hover:shadow-lg dark:border-slate-700 dark:bg-slate-800 fade-in-up-ready ${isInView ? 'in-view' : ''}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {/* Icon */}
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10 text-primary transition-colors group-hover:bg-primary/20">
        <IconComponent className="h-6 w-6" />
      </div>

      {/* Value */}
      <div className="mb-2">
        <span className="text-3xl font-bold text-slate-900 dark:text-slate-100 sm:text-4xl">
          {prefix}
          {animatedValue.toLocaleString('pt-BR')}
          {suffix}
        </span>
      </div>

      {/* Label */}
      <p className="text-sm font-medium text-slate-600 dark:text-slate-400">{t(translationKey)}</p>

      {/* Decorative element */}
      <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-primary/5 blur-2xl opacity-0 transition-opacity group-hover:opacity-100"></div>
    </div>
  );
}
